import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, GripVertical, TimerOff, Timer } from 'lucide-react';
import { usePreferences } from '../../context/PreferencesContext';

const DashboardLayout = ({ id, title, icon: Icon, onRefresh, loading, error, children }) => {
  const storageKey = `pmd-dashboard-refresh-${id}`;
  const { hydrated: prefsHydrated, getPref, setPref } = usePreferences();
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved !== null ? saved === 'true' : true;
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const hasLoadedOnce = useRef(false);
  const refreshIntervalRef = useRef(null);
  const menuRef = useRef(null);

  // Adopt the server-backed flag once hydrated (general.dashboardRefresh is a
  // json map keyed by dashboard id; localStorage stays as the boot cache).
  const prefAdopted = useRef(false);
  useEffect(() => {
    if (!prefsHydrated || prefAdopted.current) return;
    prefAdopted.current = true;
    try {
      const map = JSON.parse(getPref('general.dashboardRefresh') || '{}');
      if (typeof map[id] === 'boolean') {
        setAutoRefresh(map[id]);
        localStorage.setItem(storageKey, String(map[id]));
      }
    } catch { /* malformed server value — keep boot-cache state */ }
  }, [prefsHydrated, getPref, id, storageKey]);

  const prevLoading = useRef(loading);
  useEffect(() => {
    if (prevLoading.current && !loading) {
      hasLoadedOnce.current = true;
      setLastUpdated(new Date());
    }
    prevLoading.current = loading;
  }, [loading]);

  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    if (autoRefresh && onRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        onRefresh();
      }, 30000);
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, onRefresh]);

  useEffect(() => {
    if (onRefresh) onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh(prev => {
      const next = !prev;
      localStorage.setItem(storageKey, String(next));
      try {
        const map = JSON.parse(getPref('general.dashboardRefresh') || '{}');
        map[id] = next;
        setPref('general.dashboardRefresh', JSON.stringify(map));
      } catch { /* server persist best-effort; boot cache already updated */ }
      return next;
    });
    setMenuOpen(false);
  }, [storageKey, id, getPref, setPref]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showLoading = loading && !hasLoadedOnce.current;

  if (showLoading) {
    return (
      <div className="stui-dashboard-page">
        <div className="stui-module-header">
          <div className="stui-module-header-left">
            <h1>{Icon && <Icon size={20} />}{title}</h1>
          </div>
        </div>
        <div className="stui-module-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stui-dashboard-page">
        <div className="stui-module-header">
          <div className="stui-module-header-left">
            <h1>{Icon && <Icon size={20} />}{title}</h1>
          </div>
        </div>
        <div className="stui-module-error">
          <p>{error}</p>
          <button className="stui-crud-btn" onClick={onRefresh}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="stui-dashboard-page">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1>{Icon && <Icon size={20} />}{title}</h1>
          {lastUpdated && (
            <span className="stui-module-header-meta">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="stui-module-header-actions">
          <button
            className="stui-crud-btn"
            onClick={onRefresh}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'stui-spinner' : ''} />
          </button>
          <div className="stui-action-menu" ref={menuRef}>
            <button
              className="stui-action-menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              title="Options"
            >
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={toggleAutoRefresh}>
                  {autoRefresh ? <TimerOff size={16} /> : <Timer size={16} />}
                  <span>{autoRefresh ? 'Disable Auto-Refresh' : 'Enable Auto-Refresh'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-dashboard-grid">
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;
